import { Link } from "@clockwise/design-system";
import { OpenInNew } from "@clockwise/design-system/icons";
import { AI_CHAT_FEED_EMPTY_MESSAGE } from "@clockwise/web-commons/src/assets/copy/AIChatFeedEmptyMessage";
import { Tooltip } from "@material-ui/core";
import { animated, useSpring } from "@react-spring/web";
import { DateTime } from "luxon";
import React, { useEffect } from "react";
import { useFeatureFlag } from "../../../launch-darkly";
import { useUserProfile } from "../../hooks/useUserProfile";
import { LottiePlayer } from "../../lottie-player/LottiePlayer";
import { AI_EXCLUSION_TOOLTIP, aiSchedulerRoadmapURL } from "../constants";
import EmptyStateAnimation from "./assets/empty-state-v2.lottie";
import { SendChatMessageButton } from "./components/SendChatMessageButton";
import {
  LIST_OF_CHAT_RECOMMENDATIONS,
  SendChatRecommendationButton,
  FIX_MY_CONFLICTS_RECOMMENDATION as fixMyConflictRecommendation,
} from "./components/SendChatRecommendationButton";
import { getRelativeTimeOfDayString } from "./utils/getRelativeTimeOfDayString";

export const FeedEmptyMessage = ({
  isImpersonatedUser,
  shouldExcludeAi,
}: {
  isImpersonatedUser: boolean;
  shouldExcludeAi: boolean;
}) => {
  const [isOnAIChatNUX] = useFeatureFlag("AIChatNUX");
  const [isOnDemandConflictResolution] = useFeatureFlag("OnDemandConflictResolution");
  const showFixMyConflicts = isOnAIChatNUX && isOnDemandConflictResolution;

  const {
    userProfile: { givenName },
  } = useUserProfile();

  const [emptyMessageContainer, emptyMessageContainerApi] = useSpring(() => ({
    from: { opacity: 0, translateY: -20 },
    leave: { opacity: 0, translateY: -50 },
  }));

  const [contentDelay, contentDelayApi] = useSpring(() => ({
    from: { opacity: 0, translateY: -20 },
    leave: { opacity: 0, translateY: -50 },
  }));

  useEffect(() => {
    emptyMessageContainerApi.start({ to: { opacity: 1, translateY: 0 }, delay: 1000 });
  }, [emptyMessageContainerApi]);

  useEffect(() => {
    contentDelayApi.start({ to: { opacity: 1, translateY: 0 }, delay: 1750 });
  }, [contentDelayApi]);

  const now = DateTime.now();
  const timeOfDay = getRelativeTimeOfDayString(now);

  const welcomeText = !!givenName ? `Good ${timeOfDay}, ${givenName}!` : `Good ${timeOfDay}!`;

  const interactionDisabled = isImpersonatedUser || shouldExcludeAi;

  return (
    <div className="cw-flex cw-flex-col cw-w-full cw-h-full cw-items-center cw-text-center">
      <animated.div
        style={emptyMessageContainer}
        className="cw-flex cw-flex-col cw-w-full cw-h-full cw-items-center cw-text-center"
      >
        <LottiePlayer
          autoplay
          loop={2}
          src={EmptyStateAnimation}
          className="cw-w-[150px] cw-h-[80px]"
          hover
        />
        <div className="cw-heading-base cw-font-body cw-text-default cw-pt-4 cw-pb-1 cw-font-bold">
          {" "}
          {welcomeText}
        </div>
      </animated.div>
      <animated.div
        style={contentDelay}
        className="cw-flex cw-flex-col cw-w-full cw-h-full cw-items-center cw-text-center"
      >
        {isOnAIChatNUX ? (
          <div className="cw-body-sm cw-mb-5">
            Clockwise AI Scheduler is here to help you schedule the impossible.
          </div>
        ) : (
          <div className="cw-body-sm cw-mb-4">
            Clockwise AI Scheduler is here to help you make time for what matters. Let's get
            started:
          </div>
        )}

        {showFixMyConflicts && fixMyConflictRecommendation && (
          <SendChatRecommendationButton
            key={fixMyConflictRecommendation.text}
            text={fixMyConflictRecommendation.text}
            html={fixMyConflictRecommendation.html}
            icon={
              interactionDisabled
                ? fixMyConflictRecommendation.iconDisabled
                : fixMyConflictRecommendation.icon
            }
            disabled={interactionDisabled}
          />
        )}

        {isOnAIChatNUX && !isOnDemandConflictResolution && (
          <div className="cw-w-full cw-flex cw-items-center cw-justify-center cw-mb-4">
            <div className="cw-grow cw-border cw-border-t-0 cw-border-b cw-border-solid cw-border-default" />
            <div className="cw-caption cw-text-subtle cw-px-3">Getting started? Try these:</div>
            <div className="cw-grow cw-border cw-border-t-0 cw-border-b cw-border-solid cw-border-default" />
          </div>
        )}

        {((isOnAIChatNUX && LIST_OF_CHAT_RECOMMENDATIONS) || []).map((recommendation) => (
          <SendChatRecommendationButton
            key={recommendation.text}
            text={recommendation.text}
            html={recommendation.html}
            icon={interactionDisabled ? recommendation.iconDisabled : recommendation.icon}
            disabled={interactionDisabled}
          />
        ))}
        {!isOnAIChatNUX && (
          <Tooltip title={shouldExcludeAi ? AI_EXCLUSION_TOOLTIP : ""}>
            <div className="cw-flex cw-flex-col cw-items-center">
              <div className="cw-my-2">
                <SendChatMessageButton
                  text="How does AI Scheduler work?"
                  disabled={interactionDisabled}
                />
              </div>
              <div className="cw-my-2">
                <SendChatMessageButton
                  text="What can you help me do?"
                  disabled={interactionDisabled}
                />
              </div>
            </div>
          </Tooltip>
        )}

        {!isOnAIChatNUX && (
          <div className="cw-flex cw-w-full cw-items-center">
            <div className="cw-w-full cw-border cw-border-t-0 cw-border-b cw-border-solid cw-border-default cw-my-8" />
          </div>
        )}

        {isOnAIChatNUX ? (
          <div className="cw-mt-5 cw-caption cw-text-subtle cw-max-w-sm">
            AI Scheduler is in Beta and is constantly being improved.{" "}
            <div>
              <Link href={aiSchedulerRoadmapURL} target="_blank">
                See what's next <OpenInNew className="cw-w-3 cw-h-3 cw-mb-[-2px]" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="cw-caption cw-text-subtle">{AI_CHAT_FEED_EMPTY_MESSAGE}</div>
        )}
      </animated.div>
    </div>
  );
};
